<template>
  <div>
    <!-- Modal Trigger Button -->
    <b-button @click="showModal = true" variant="primary" class="add-resource-btn">
      <feather-icon icon="PlusIcon" size="16" />
    </b-button>

    <!-- Create HS Code Modal -->
    <b-modal
      v-model="showModal"
      title="Create New HS Code"
      size="xl"
      :hide-footer="true"
      :hide-header="true"
      :hide-header-close="true"
      :no-close-on-backdrop="true"
      :no-close-on-esc="true"
      centered
      @hidden="onModalHidden"
      id="create-hs-code-modal"
    >
      <!-- Modal Body -->
      <div class="modal-body p-0">
        <HSCodeForm
          :form-data="hsCodeFormData"
          :loading="loading"
          :modal-mode="true"
          @submit="handleSubmit"
          @update:form-data="updateFormData"
          @cancel="closeModal"
          :is-modal="true"
        />
      </div>
    </b-modal>
  </div>
</template>

<script>
import { BModal, BButton, BSpinner } from 'bootstrap-vue';
import { ref, reactive } from '@vue/composition-api';
import { useToast } from 'vue-toastification/composition';
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue';
import FeatherIcon from '@core/components/feather-icon/FeatherIcon.vue';
import HSCodeForm from './HSCodeForm.vue';
import store from '@/store';
import HSCodeStoreModule from './HSCodeStoreModule.js';

export default {
  name: 'CreateCustomerModal',
  components: {
    BModal,
    BButton,
    BSpinner,
    FeatherIcon,
    HSCodeForm,
    ToastificationContent,
  },
  emits: ['submit'],
  setup(props, { emit }) {
    const toast = useToast();

    // Reactive state
    const showModal = ref(false);
    const loading = ref(false);
    const hsCodeFormData = reactive({
      id: 0,
      name: '',
      code: '',
      description: '',
      isActive: true,
    });

    // Methods
    const updateFormData = (newData) => {
      Object.assign(hsCodeFormData, newData);
    };

    const handleSubmit = async () => {
      loading.value = true;

      try {
        // Call the store action to create customer
        const hsCodeResponse = await store.dispatch('hs-code/addHSCode', hsCodeFormData);

        // Show success message
        toast({
          component: ToastificationContent,
          props: {
            title: 'Success',
            text: 'HS Code created successfully!',
            variant: 'success',
            icon: 'CheckIcon',
          },
        });

        // Reset form data
        resetFormData();

        // Close modal
        closeModal();

        // Emit submit event to parent
        emit('submit', hsCodeResponse);
      } catch (error) {
        console.error('Error creating HS Code:', error);

        // Show error message
        toast({
          component: ToastificationContent,
          props: {
            title: 'Error',
            text: error?.response?.data?.message || 'Failed to create HS Code. Please try again.',
            variant: 'danger',
            icon: 'AlertTriangleIcon',
          },
        });
      } finally {
        loading.value = false;
      }
    };

    const resetFormData = () => {
      Object.assign(hsCodeFormData, {
        id: 0,
        code: '',
        name: '',
        description: '',
        isActive: true,
      });
    };

    const closeModal = () => {
      showModal.value = false;
    };

    const onModalHidden = () => {
      // Reset form when modal is hidden
      resetFormData();
    };

    return {
      // Reactive state
      showModal,
      loading,
      hsCodeFormData,

      // Methods
      updateFormData,
      handleSubmit,
      resetFormData,
      closeModal,
      onModalHidden,
    };
  },
};
</script>

<style lang="scss" scoped>
.modal-body {
  max-height: 60vh;
  overflow-y: auto;
  padding: 0;
}

::v-deep .modal-xl {
  max-width: 95%;
}

::v-deep .modal-header {
  border-bottom: 1px solid #dee2e6;
  padding: 1rem 1.5rem;
}

::v-deep .modal-footer {
  border-top: 1px solid #dee2e6;
  padding: 1rem 1.5rem;
}
</style>
